import { useMemo, useState } from 'react'
import { useSession } from '@/core/auth/SessionStore'
import { AlertTriangle, Loader2, X } from 'lucide-react'
import { Button } from '@/core/ui/Button'
import { PickerSheet } from '@/features/common/PickerSheet'
import type { EquipeParteDto, ObraParteDto, ServicoParteDto } from '@/types/parteDiariaDtos'
import {
  contextoMve,
  errosDaLinha,
  formularioMedicao,
  iniciosDasLinhas,
  origemHorimetro,
  papelDaLinha,
  type LinhaMveDraft,
  type ParteDiariaState,
} from './parteDiariaStore'
import { useEquipesParteDiaria, useObrasParteDiaria, useServicosParteDiaria } from './useParteDiaria'
import { servicoExigeDiferencaZero, type CampoLinha } from './parteDiariaRegras'
import { LabeledInput, NumericField, Pair, PickerField, ReadOnlyField, Section } from './fields'
import { fmtNum, parseNum } from './format'

/**
 * Formulário de uma linha de mvt_mve (principal ou fração), como a linha da
 * grade do SIGA: obra → serviço → início (só leitura, encadeado) → término →
 * diferença → chapa → equipe → horas → ficha (M+C) → observação.
 */

interface Encarregado {
  id: number
  nome: string
  qtd: number
}

interface Props {
  state: ParteDiariaState
  indice: number
  initial: LinhaMveDraft
  destaque?: { campo: string | null; mensagem: string } | null
  onSave: (l: LinhaMveDraft) => void
  onDismiss: () => void
}

export function LinhaMveFormSheet({ state, indice, initial, destaque, onSave, onDismiss }: Props) {
  const ctx = contextoMve(state)
  const papel = papelDaLinha(state, indice)
  const formulario = formularioMedicao(state)
  const [linha, setLinha] = useState<LinhaMveDraft>(initial)
  const [termino, setTermino] = useState(initial.medicaoTermino?.toString() ?? '')
  const [picker, setPicker] = useState<'obra' | 'servico' | 'encarregado' | 'equipe' | null>(null)

  const obrasOnline = useObrasParteDiaria(!ctx, state.mveId)
  // Obras do projeto atual (o da Home) primeiro: chefe vinculado a vários projetos quase sempre lança no atual.
  const projetoAtualId = useSession()?.projetoAtualId ?? null
  const obras: ObraParteDto[] = useMemo(() => {
    const lista = ctx?.obras ?? obrasOnline.items
    if (projetoAtualId == null) return lista
    return [...lista].sort((a, b) => Number(b.projetoId === projetoAtualId) - Number(a.projetoId === projetoAtualId))
  }, [ctx, obrasOnline.items, projetoAtualId])
  const servicos = useServicosParteDiaria(state.mveId, linha.obraId)
  const equipes = useEquipesParteDiaria(linha.obraId)

  // Encarregado: vem de cdt_projeto_tem_equipe (um por equipe no projeto). Se a
  // linha não guardou o encarregado (rascunho antigo / reaberto), deduz pela equipe.
  const encarregadoId = linha.encarregadoId ?? equipes.items.find((e) => e.id === linha.equipeId)?.encarregadoId ?? null
  const encarregados: Encarregado[] = []
  for (const e of equipes.items) {
    if (e.encarregadoId == null || encarregados.some((x) => x.id === e.encarregadoId)) continue
    encarregados.push({
      id: e.encarregadoId,
      nome: e.encarregadoNome ?? `Encarregado ${e.encarregadoId}`,
      qtd: equipes.items.filter((x) => x.encarregadoId === e.encarregadoId).length,
    })
  }
  const encarregadoNome = linha.encarregadoNome ?? encarregados.find((x) => x.id === encarregadoId)?.nome ?? null
  const equipesFiltradas = encarregadoId != null ? equipes.items.filter((e) => e.encarregadoId === encarregadoId) : equipes.items

  function escolherEncarregado(enc: Encarregado) {
    const dele = equipes.items.filter((e) => e.encarregadoId === enc.id)
    setLinha((l) => {
      const mantem = dele.some((e) => e.id === l.equipeId)
      // Só uma equipe do encarregado → já seleciona.
      const unica = dele.length === 1 ? dele[0] : null
      return {
        ...l,
        encarregadoId: enc.id,
        encarregadoNome: enc.nome,
        equipeId: mantem ? l.equipeId : (unica?.id ?? null),
        equipeNome: mantem ? l.equipeNome : (unica?.nome ?? null),
      }
    })
    setPicker(null)
  }

  const atual: LinhaMveDraft = { ...linha, medicaoTermino: parseNum(termino) }
  const estadoTemp: ParteDiariaState = { ...state, linhas: state.linhas.map((l, i) => (i === indice ? atual : l)) }
  const erros = errosDaLinha(estadoTemp, indice)
  const erroDe = (campo: CampoLinha) => erros.find((e) => e.campo === campo)?.mensagem ?? null
  const destacado = (campo: CampoLinha) => destaque?.campo === campo
  const inicio = iniciosDasLinhas(estadoTemp)[indice] ?? null
  const diferenca = atual.medicaoTermino != null && inicio != null ? atual.medicaoTermino - inicio : null
  const exigeZero = servicoExigeDiferencaZero(linha.servico ? { ...linha.servico, grupoNome: linha.servico.grupoNome } : null)
  const podeSalvar = erros.length === 0
  const projetoOutro =
    ctx && linha.obraId != null
      ? (() => {
          const o = ctx.obras.find((x) => x.id === linha.obraId)
          return o && ctx.dia.linhasHoje[0]?.obraId != null && ctx.obras.find((x) => x.id === ctx.dia.linhasHoje[0]?.obraId)?.projetoId !== o.projetoId
        })()
      : false

  function escolherObra(o: ObraParteDto) {
    setLinha((l) => ({
      ...l,
      obraId: o.id,
      obraNome: `${o.codigo}${o.nome ? ` · ${o.nome}` : ''}`,
      projetoCmve: o.projetoCmve,
      // serviço e equipe dependem da obra
      servico: null,
      encarregadoId: null,
      encarregadoNome: null,
      equipeId: null,
      equipeNome: null,
    }))
    setPicker(null)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center bg-black/60 backdrop-blur-sm" onClick={onDismiss}>
      <div className="card-surface w-full sm:max-w-md max-h-[90vh] rounded-t-2xl sm:rounded-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-center pt-3 pb-1 sm:hidden">
          <div className="h-1 w-12 rounded-full bg-on-surface-muted" />
        </div>
        <div className="flex items-start justify-between px-4 pt-4 pb-3 border-b border-border">
          <div className="flex-1 min-w-0 pr-2">
            <p className="text-xs font-bold text-accent uppercase">
              {papel === 'PRINCIPAL' ? 'Linha principal' : `Fração ${indice + 1}`} · {state.mvePrefixo ?? ''}
            </p>
            <h2 className="text-lg font-bold text-on-surface mt-1 truncate">Dia {Number(state.dt.slice(8, 10))}</h2>
            <p className="text-xs text-on-surface-dim mt-0.5">
              {formulario === 'P' ? 'Produção (decimal)' : 'Horímetro / odômetro (inteiro)'}
              {ctx?.contratoMes?.unidade ? ` · ${ctx.contratoMes.unidade}` : ''}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onDismiss} aria-label="Fechar">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {destaque && (
            <div className="rounded-lg border border-bad bg-bad/10 px-3 py-2 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-bad shrink-0 mt-0.5" />
              <p className="text-xs text-on-surface">{destaque.mensagem}</p>
            </div>
          )}

          <Section title="ONDE">
            <PickerField
              label="Obra"
              value={linha.obraNome || 'Selecionar…'}
              onClick={() => setPicker('obra')}
              erro={erroDe('obra')}
              destacado={destacado('obra')}
              badge={linha.projetoCmve ? 'CMVE' : null}
            />
            {projetoOutro && (
              <p className="text-xs text-warn px-1">
                Obra de outro projeto: a linha fica em nome do chefe desse projeto na data (regra do SIGA).
              </p>
            )}
            <PickerField
              label="Serviço"
              value={linha.servico?.nome ?? 'Selecionar…'}
              sublabel={
                linha.servico
                  ? [linha.servico.isento ? 'isento (parada)' : null, exigeZero ? 'diferença deve ser zero' : null].filter(Boolean).join(' · ') || null
                  : null
              }
              onClick={() => linha.obraId != null && setPicker('servico')}
              disabled={linha.obraId == null}
              erro={erroDe('servico')}
              destacado={destacado('servico')}
            />
            <PickerField
              label="Encarregado"
              value={encarregadoNome ?? 'Todos'}
              sublabel={encarregadoId != null ? `${equipesFiltradas.length} equipe(s)` : 'filtra a lista de equipes'}
              onClick={() => linha.obraId != null && setPicker('encarregado')}
              disabled={linha.obraId == null || encarregados.length === 0}
            />
            <PickerField
              label={papel === 'PRINCIPAL' ? 'Equipe' : 'Equipe (opcional na fração)'}
              value={linha.equipeNome ?? (linha.equipeId != null ? `Equipe ${linha.equipeId}` : 'Selecionar…')}
              onClick={() => linha.obraId != null && setPicker('equipe')}
              disabled={linha.obraId == null}
              erro={erroDe('equipe')}
              destacado={destacado('equipe')}
            />
          </Section>

          <Section title="MEDIÇÃO">
            <Pair>
              <ReadOnlyField
                label="Início"
                value={formulario === 'P' ? '—' : inicio != null ? fmtNum(inicio, 0) : 'definido no envio'}
                hint={
                  formulario === 'P'
                    ? 'sem início em produção'
                    : indice > 0
                      ? 'término da linha anterior'
                      : { siga: 'sugerido pelo SIGA', cache: 'último do SIGA (em cache)', resumo: 'último conhecido (offline)', pendente: 'término do seu último envio pendente' }[
                          origemHorimetro(state) ?? 'siga'
                        ]
                }
              />
              <NumericField
                label="Término"
                value={termino}
                onChange={setTermino}
                integer={formulario !== 'P'}
                erro={erroDe('medicaoTermino')}
                destacado={destacado('medicaoTermino') || destacado('medicaoInicio')}
              />
            </Pair>
            <div className="card-surface-variant px-4 py-2 flex items-center justify-between">
              <span className="text-xs text-on-surface-dim uppercase">Diferença</span>
              <span className={`font-mono tabular text-lg ${diferenca != null && diferenca < 0 ? 'text-bad' : 'text-on-surface'}`}>
                {diferenca == null ? '—' : fmtNum(diferenca, formulario === 'P' ? 2 : 0)}
              </span>
            </div>
            <LabeledInput
              label="Chapa op./mot."
              value={linha.chapa ?? ''}
              onChange={(v) => /^[0-9]*$/.test(v) && setLinha((l) => ({ ...l, chapa: v || null }))}
              placeholder="000000"
              maxLength={10}
              inputMode="numeric"
            />
          </Section>

          <Section title="HORÁRIO">
            <Pair>
              <LabeledInput
                label="Hora início"
                value={linha.hInicio ?? ''}
                onChange={(v) => setLinha((l) => ({ ...l, hInicio: v || null }))}
                type="time"
                erro={erroDe('hInicio')}
                destacado={destacado('hInicio')}
              />
              <LabeledInput
                label="Hora fim"
                value={linha.hFim ?? ''}
                onChange={(v) => setLinha((l) => ({ ...l, hFim: v || null }))}
                type="time"
                erro={erroDe('hFim')}
                destacado={destacado('hFim')}
              />
            </Pair>
          </Section>

          <Section title="OUTROS">
            {ctx?.mve.apresentarFicha && (
              <p className="text-xs text-on-surface-dim px-1">
                Caminhão com viagens: lance a ficha no módulo <span className="font-semibold text-on-surface">Carreteiro</span>. O número
                da ficha e as viagens entram nesta linha automaticamente.
              </p>
            )}
            <LabeledInput
              label="Observação"
              value={linha.observacao ?? ''}
              onChange={(v) => setLinha((l) => ({ ...l, observacao: v || null }))}
              maxLength={500}
            />
          </Section>
        </div>

        <div className="border-t border-border px-4 py-3 space-y-2">
          {!podeSalvar && (
            <p className="text-xs text-warn text-center">{Array.from(new Set(erros.map((e) => e.mensagem))).join(' · ')}</p>
          )}
          <div className="flex gap-3">
            <Button variant="outline" size="lg" onClick={onDismiss} className="flex-1">
              Cancelar
            </Button>
            <Button variant="primary" size="lg" onClick={() => podeSalvar && onSave(atual)} disabled={!podeSalvar} className="flex-1">
              Salvar linha
            </Button>
          </div>
        </div>
      </div>

      {picker === 'obra' && (
        <PickerSheet<ObraParteDto>
          open
          title="Obra"
          items={obras}
          label={(o) => o.codigo}
          sublabel={(o) => [o.nome, o.projetoCmve ? 'CMVE' : null].filter(Boolean).join(' · ') || null}
          onPick={escolherObra}
          onDismiss={() => setPicker(null)}
          searchHint="Buscar obra"
          emptyMessage={obrasOnline.loading ? 'Carregando obras…' : 'Nenhuma obra ativa para o chefe de equipe'}
        />
      )}
      {picker === 'servico' && (
        <PickerSheet<ServicoParteDto>
          open
          title="Serviço executado"
          items={servicos.items}
          label={(s) => s.nome}
          sublabel={(s) => [s.grupoNome, s.isento ? 'isento' : null].filter(Boolean).join(' · ') || null}
          onPick={(s) => {
            setLinha((l) => ({ ...l, servico: s }))
            setPicker(null)
          }}
          onDismiss={() => setPicker(null)}
          searchHint="Buscar serviço"
          emptyMessage={servicos.loading ? 'Carregando serviços…' : servicos.source == null ? 'Sem rede e sem cache de serviços desta obra.' : 'Nenhum serviço para esta MVE e obra'}
          header={
            servicos.loading ? (
              <div className="px-4 py-2 text-xs text-on-surface-dim flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" /> Buscando serviços…
              </div>
            ) : servicos.source === 'cache' ? (
              <div className="px-4 py-2 text-xs text-warn">Sem rede — lista em cache.</div>
            ) : null
          }
        />
      )}
      {picker === 'encarregado' && (
        <PickerSheet<Encarregado>
          open
          title="Encarregado"
          items={encarregados}
          label={(x) => x.nome}
          sublabel={(x) => `${x.qtd} equipe(s) no projeto`}
          onPick={escolherEncarregado}
          onDismiss={() => setPicker(null)}
          searchHint="Buscar encarregado"
          emptyMessage="Nenhum encarregado nas equipes desta obra"
          header={
            encarregadoId != null ? (
              <button
                type="button"
                className="w-full text-left px-4 py-2 text-xs text-accent"
                onClick={() => {
                  setLinha((l) => ({ ...l, encarregadoId: null, encarregadoNome: null }))
                  setPicker(null)
                }}
              >
                Limpar filtro (todas as equipes)
              </button>
            ) : null
          }
        />
      )}
      {picker === 'equipe' && (
        <PickerSheet<EquipeParteDto>
          open
          title={encarregadoNome ? `Equipes de ${encarregadoNome}` : 'Equipe'}
          items={equipesFiltradas}
          label={(e) => e.nome}
          sublabel={(e) => (encarregadoId == null && e.encarregadoNome ? e.encarregadoNome : null)}
          onPick={(e) => {
            setLinha((l) => ({
              ...l,
              equipeId: e.id,
              equipeNome: e.nome,
              encarregadoId: e.encarregadoId ?? l.encarregadoId ?? null,
              encarregadoNome: e.encarregadoNome ?? l.encarregadoNome ?? null,
            }))
            setPicker(null)
          }}
          onDismiss={() => setPicker(null)}
          searchHint="Buscar equipe"
          emptyMessage={equipes.loading ? 'Carregando equipes…' : equipes.source == null ? 'Sem rede e sem cache de equipes desta obra.' : 'Nenhuma equipe ativa no projeto da obra'}
        />
      )}
    </div>
  )
}
